import React, { useState } from 'react';
import { motion, AnimatePresence } from "framer-motion";
import Banner from '../Components/Banner';
import { ChevronRight } from 'lucide-react';
import { Helmet } from "react-helmet-async";
import LocationForm from '../Components/mumbai/LocationForm';
import ServiceNowLeadForm from '../Components/ServiceNowLeadForm';

function DedicatedServiceNowTeam() {
const servicenowFaqData = [
    {
        id: 1,
        question: "What is a dedicated ServiceNow team?",
        answer:
            "A dedicated ServiceNow team is a group of professionals assigned to your specific projects and long-term ServiceNow requirements."
    },
    {
        id: 2,
        question: "What professionals can be included in a dedicated ServiceNow team?",
        answer:
            "A team can include ServiceNow developers, consultants, architects, administrators, integration specialists, business analysts, and QA professionals."
    },
    {
        id: 3,
        question: "Can I build a dedicated ServiceNow team for a specific project?",
        answer:
            "Yes. Dedicated teams can be built for ITSM, ITOM, CMDB, custom applications, integrations, workflow automation, platform enhancements, and other ServiceNow projects."
    },
    {
        id: 4,
        question: "Can a dedicated ServiceNow team work with our internal team?",
        answer:
            "Yes. Our professionals can work as an extension of your internal IT and development teams while following your tools, processes, and technical standards."
    },
    {
        id: 5,
        question: "Can I scale my dedicated ServiceNow team?",
        answer:
            "Yes. Team size and expertise can be adjusted based on your project scope, workload, technical requirements, and long-term ServiceNow needs."
    },
    {
        id: 6,
        question: "Why choose Mobikode for a dedicated ServiceNow team?",
        answer:
            "Mobikode provides flexible dedicated ServiceNow teams covering development, consulting, architecture, administration, integrations, and QA based on your project requirements."
    }
];
    
          const [activeIndex, setActiveIndex] = useState(0);
          const [faqActiveIndex, setFaqActiveIndex] = useState(0);
    
    const toggleFaqAccordion = (index) => {
        setFaqActiveIndex(faqActiveIndex === index ? -1 : index);
    };
  return (
    
    <div>
<Helmet>
    <title>
        Dedicated ServiceNow Team | Mobikode
    </title>

    <meta
        name="description"
        content="Build a dedicated ServiceNow team with experienced developers, consultants, architects, administrators, integration specialists, and QA professionals through Mobikode."
    />

    <meta
        name="keywords"
        content="dedicated ServiceNow team, dedicated ServiceNow developers, dedicated ServiceNow team services, ServiceNow team augmentation, ServiceNow dedicated development team, ServiceNow remote team, ServiceNow offshore team, ServiceNow development team, ServiceNow consultants, ServiceNow architects, ServiceNow administrators, ServiceNow integration team, ServiceNow outsourcing"
    />

    <link
        rel="canonical"
        href="https://www.mobikode.com/dedicated-servicenow-team/"
    />

    {/* Open Graph */}
    <meta
        property="og:title"
        content="Dedicated ServiceNow Team | Mobikode"
    />

    <meta
        property="og:description"
        content="Build a dedicated ServiceNow team with experienced developers, consultants, architects, administrators, integration specialists, and QA professionals aligned with your project requirements."
    />

    <meta
        property="og:url"
        content="https://www.mobikode.com/dedicated-servicenow-team/"
    />

    <meta
        property="og:type"
        content="website"
    />

    <meta
        property="og:site_name"
        content="Mobikode"
    />

    <meta
        property="og:image"
        content="https://www.mobikode.com/assets/services/dedicated-servicenow-team.jpg"
    />

    <meta
        property="og:image:alt"
        content="Dedicated ServiceNow Team by Mobikode"
    />

    {/* Twitter / X */}
    <meta
        name="twitter:card"
        content="summary_large_image"
    />

    <meta
        name="twitter:title"
        content="Dedicated ServiceNow Team | Mobikode"
    />

    <meta
        name="twitter:description"
        content="Build a dedicated ServiceNow team with developers, consultants, architects, administrators, integration specialists, and QA professionals."
    />

    <meta
        name="twitter:image"
        content="https://www.mobikode.com/assets/services/dedicated-servicenow-team.jpg"
    />

    <meta
        name="twitter:image:alt"
        content="Dedicated ServiceNow Team by Mobikode"
    />
</Helmet>


<section className="relative h-[500px] lg:h-[630px] w-full overflow-hidden bg-[#0a0b1e]">
    <div className="absolute inset-0 z-0">
        <img
            src="/assets/services/bg3.png"
            alt="Dedicated ServiceNow team for enterprise development and support"
            className="h-full w-full object-cover opacity-40"
        />

        <div className="absolute inset-0 bg-gradient-to-b lg:bg-gradient-to-r from-[#0a0b1e] via-[#0a0b1e]/80 to-transparent opacity-90" />
    </div>

    <div className="relative z-10 flex h-full max-w-[1440px] w-full mx-auto flex-col justify-between px-6 lg:px-20 pt-20 pb-[60px]">

        {/* Hero Heading */}
        <motion.div
            initial={{ opacity: 0, x: -60 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            viewport={{ once: true }}
            className="lg:mt-[115px] mt-[40px]"
        >
            <h1 className="text-[38px] lg:text-[55px] text-[#FFFFFF] max-w-[800px] w-full font-sora font-[600] leading-[1.2] mb-[22px]">
                Dedicated ServiceNow Team
            </h1>

            <p className="text-[#FFFFFF] font-graphik text-[16px] md:text-[20px] font-[400] leading-[1.6] mb-4">
                Build a Dedicated ServiceNow Team for Your Business
            </p>
        </motion.div>

        {/* Hero Description */}
        <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
            className="mt-12 lg:self-end max-w-full lg:max-w-[501px] lg:ml-auto text-left md:mt-0"
        >
            <p className="text-[#FFFFFF] font-graphik text-[14px] md:text-[16px] font-[400] leading-[1.6]">
                Build a dedicated ServiceNow team with experienced developers,
                consultants, architects, administrators, integration specialists,
                and QA professionals. Mobikode provides flexible dedicated
                ServiceNow teams that work alongside your organization to support
                implementations, development, integrations, enhancements, and
                long-term ServiceNow initiatives.
            </p>
        </motion.div>

    </div>
</section>

<section className="bg-[#FFFFFF] overflow-hidden lg:py-[100px] py-[60px]">
    <div className="max-w-[1440px] w-full mx-auto lg:px-[80px] px-[20px]">

        <div className="flex flex-col lg:flex-row items-center gap-[40px] lg:gap-[80px]">

            {/* LEFT IMAGE */}
            <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="w-full lg:w-1/2"
            >
                <div className="w-full h-[280px] md:h-[380px] lg:h-[450px] overflow-hidden">
                    <img
                        src="/assets/sales/img2.png"
                        alt="Dedicated ServiceNow team for development and consulting"
                        className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                    />
                </div>
            </motion.div>

            {/* RIGHT CONTENT */}
            <div className="w-full lg:w-1/2 text-left">

                <motion.div
                    initial={{ opacity: 0, y: -15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="flex items-center gap-[10px] pb-[15px] lg:pb-[20px]"
                >
                    <motion.span className="font-[500] font-graphik text-[16px] lg:text-[20px] leading-[1.6] text-[#01386A]">
                        Service Overview
                    </motion.span>
                </motion.div>

                <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 }}
                    className="font-[600] font-sora lg:text-[30px] max-w-[850px] w-full text-[20px] leading-[1.3] text-[#01386A]"
                >
                    Build a Dedicated ServiceNow Team for Your Projects
                </motion.h2>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.35 }}
                    className="font-[400] max-w-[800px] w-full font-graphik text-[14px] lg:text-[16px] leading-[1.6] lg:leading-[22px] text-[#01386A] mt-[20px] lg:mt-[25px]"
                >
                    Mobikode provides dedicated ServiceNow teams that work as an
                    extension of your organization. Build a team of experienced
                    ServiceNow developers, consultants, architects, administrators,
                    integration specialists, and QA professionals based on your
                    project and technical requirements.

                    <br /><br />

                    A dedicated ServiceNow team can support long-term development,
                    platform enhancements, implementations, integrations, workflow
                    automation, maintenance, and ongoing ServiceNow initiatives.
                    Our flexible team model allows you to build the right mix of
                    ServiceNow expertise around your business goals and delivery
                    requirements.
                </motion.p>

                <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: "70px" }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.5, duration: 0.6 }}
                    className="h-[3px] bg-[#ED6800] mt-[25px] lg:mt-[35px]"
                />

            </div>

        </div>

    </div>
</section>

<section className="w-full bg-[#FDFDFD] py-16 lg:py-24 relative">
    <div className="max-w-[1440px] mx-auto px-[20px] lg:px-[80px] relative z-10">

        <div className="flex flex-col lg:flex-row gap-10 lg:gap-20 items-start">

            {/* LEFT SIDE */}
            <div className="w-full lg:w-[40%] lg:sticky lg:top-24">
                <h2 className="text-[#01386A] font-sora text-[20px] lg:text-[30px] font-[600] text-left">
                    Dedicated ServiceNow Team Expertise
                </h2>
            </div>

            {/* RIGHT SIDE */}
            <div className="w-full lg:w-[60%] flex flex-col gap-6">

                {/* Card 1 */}
                <div className="bg-white border border-[#E2E8F0] border-b-[4px] border-b-[#01386A] hover:border-b-[#e86c15] transition-colors duration-300 p-8 lg:px-10 lg:py-10 flex flex-col justify-center cursor-pointer group">
                    <h3 className="text-[#01386A] text-[16px] lg:text-[20px] font-[600] font-sora mb-2">
                        Dedicated ServiceNow Developers
                    </h3>
                    <p className="text-[#49577A] text-[14px] lg:text-[16px] font-[500] font-graphik leading-snug">
                        Build a dedicated development team with ServiceNow
                        developers experienced in custom applications, scripting,
                        workflows, automation, platform configuration, and ongoing
                        ServiceNow development.
                    </p>
                </div>

                {/* Card 2 */}
                <div className="bg-white border border-[#E2E8F0] border-b-[4px] border-b-[#01386A] hover:border-b-[#e86c15] transition-colors duration-300 p-8 lg:px-10 lg:py-10 flex flex-col justify-center cursor-pointer group">
                    <h3 className="text-[#01386A] text-[16px] lg:text-[20px] font-[600] font-sora mb-2">
                        Dedicated ServiceNow Consultants
                    </h3>
                    <p className="text-[#49577A] text-[14px] lg:text-[16px] font-[500] font-graphik leading-snug">
                        Add ServiceNow consultants to your dedicated team for
                        strategy, platform assessment, process optimization,
                        implementation planning, and technical consulting.
                    </p>
                </div>

                {/* Card 3 */}
                <div className="bg-white border border-[#E2E8F0] border-b-[4px] border-b-[#01386A] hover:border-b-[#e86c15] transition-colors duration-300 p-8 lg:px-10 lg:py-10 flex flex-col justify-center cursor-pointer group">
                    <h3 className="text-[#01386A] text-[16px] lg:text-[20px] font-[600] font-sora mb-2">
                        Dedicated ServiceNow Architects
                    </h3>
                    <p className="text-[#49577A] text-[14px] lg:text-[16px] font-[500] font-graphik leading-snug">
                        Strengthen your team with ServiceNow architects who can
                        support platform architecture, technical decisions,
                        integrations, scalability, customization, and complex
                        ServiceNow requirements.
                    </p>
                </div>

                {/* Card 4 */}
                <div className="bg-white border border-[#E2E8F0] border-b-[4px] border-b-[#01386A] hover:border-b-[#e86c15] transition-colors duration-300 p-8 lg:px-10 lg:py-10 flex flex-col justify-center cursor-pointer group">
                    <h3 className="text-[#01386A] text-[16px] lg:text-[20px] font-[600] font-sora mb-2">
                        Dedicated ServiceNow Administrators
                    </h3>
                    <p className="text-[#49577A] text-[14px] lg:text-[16px] font-[500] font-graphik leading-snug">
                        Extend your ServiceNow operations with dedicated
                        administrators supporting platform configuration, users,
                        access controls, workflows, maintenance, and day-to-day
                        administration.
                    </p>
                </div>

                {/* Card 5 */}
                <div className="bg-white border border-[#E2E8F0] border-b-[4px] border-b-[#01386A] hover:border-b-[#e86c15] transition-colors duration-300 p-8 lg:px-10 lg:py-10 flex flex-col justify-center cursor-pointer group">
                    <h3 className="text-[#01386A] text-[16px] lg:text-[20px] font-[600] font-sora mb-2">
                        Dedicated Integration Specialists
                    </h3>
                    <p className="text-[#49577A] text-[14px] lg:text-[16px] font-[500] font-graphik leading-snug">
                        Add ServiceNow integration specialists to support REST and
                        SOAP APIs, IntegrationHub, enterprise applications, data
                        synchronization, and automated integration workflows.
                    </p>
                </div>

                {/* Card 6 */}
                <div className="bg-white border border-[#E2E8F0] border-b-[4px] border-b-[#01386A] hover:border-b-[#e86c15] transition-colors duration-300 p-8 lg:px-10 lg:py-10 flex flex-col justify-center cursor-pointer group">
                    <h3 className="text-[#01386A] text-[16px] lg:text-[20px] font-[600] font-sora mb-2">
                        Dedicated ServiceNow QA Professionals
                    </h3>
                    <p className="text-[#49577A] text-[14px] lg:text-[16px] font-[500] font-graphik leading-snug">
                        Strengthen your ServiceNow delivery team with QA
                        professionals supporting functional testing, integration
                        testing, regression testing, validation, and quality
                        assurance across ServiceNow projects.
                    </p>
                </div>

            </div>
        </div>
    </div>
</section>

<section className="w-full bg-white py-16 lg:py-24">
    <div className="max-w-[1440px] mx-auto px-[20px] lg:px-[80px]">

        <h2 className="text-[#01386A] text-[20px] md:text-[30px] font-[600] font-sora mb-6 lg:mb-10 text-left">
            Dedicated ServiceNow Team Capabilities
        </h2>

        <div className="flex flex-col lg:flex-row gap-12 lg:gap-24">

            {/* LEFT SIDE */}
            <div className="lg:w-1/2 flex flex-col">

                {/* ITSM */}
                <div
                    onClick={() => setActiveIndex(0)}
                    className={`group cursor-pointer flex items-center justify-between py-6 lg:py-8 border-b-[3px] transition-all duration-300 ${
                        activeIndex === 0
                            ? "border-[#ED6800]"
                            : "border-gray-300 hover:border-gray-600"
                    }`}
                >
                    <h3
                        className={`text-[16px] md:text-[20px] font-[600] font-sora transition-colors duration-300 ${
                            activeIndex === 0
                                ? "text-[#01386A]"
                                : "text-[#0000008C] group-hover:text-gray-600"
                        }`}
                    >
                        Dedicated ServiceNow ITSM Team
                    </h3>

                    <ChevronRight
                        size={24}
                        className={`transition-all duration-300 ${
                            activeIndex === 0
                                ? "opacity-100 translate-x-0 text-[#ED6800]"
                                : "opacity-0 -translate-x-4 text-gray-300"
                        }`}
                    />
                </div>

                {/* ITOM */}
                <div
                    onClick={() => setActiveIndex(1)}
                    className={`group cursor-pointer flex items-center justify-between py-6 lg:py-8 border-b-[3px] transition-all duration-300 ${
                        activeIndex === 1
                            ? "border-[#ED6800]"
                            : "border-gray-300 hover:border-gray-600"
                    }`}
                >
                    <h3
                        className={`text-[16px] md:text-[20px] font-[600] font-sora transition-colors duration-300 ${
                            activeIndex === 1
                                ? "text-[#01386A]"
                                : "text-[#0000008C] group-hover:text-gray-600"
                        }`}
                    >
                        Dedicated ServiceNow ITOM & CMDB Team
                    </h3>

                    <ChevronRight
                        size={24}
                        className={`transition-all duration-300 ${
                            activeIndex === 1
                                ? "opacity-100 translate-x-0 text-[#ED6800]"
                                : "opacity-0 -translate-x-4 text-gray-300"
                        }`}
                    />
                </div>

                {/* CSM */}
                <div
                    onClick={() => setActiveIndex(2)}
                    className={`group cursor-pointer flex items-center justify-between py-6 lg:py-8 border-b-[3px] transition-all duration-300 ${
                        activeIndex === 2
                            ? "border-[#ED6800]"
                            : "border-gray-300 hover:border-gray-600"
                    }`}
                >
                    <h3
                        className={`text-[16px] md:text-[20px] font-[600] font-sora transition-colors duration-300 ${
                            activeIndex === 2
                                ? "text-[#01386A]"
                                : "text-[#0000008C] group-hover:text-gray-600"
                        }`}
                    >
                        Dedicated ServiceNow CSM Team
                    </h3>

                    <ChevronRight
                        size={24}
                        className={`transition-all duration-300 ${
                            activeIndex === 2
                                ? "opacity-100 translate-x-0 text-[#ED6800]"
                                : "opacity-0 -translate-x-4 text-gray-300"
                        }`}
                    />
                </div>

                {/* HRSD */}
                <div
                    onClick={() => setActiveIndex(3)}
                    className={`group cursor-pointer flex items-center justify-between py-6 lg:py-8 border-b-[3px] transition-all duration-300 ${
                        activeIndex === 3
                            ? "border-[#ED6800]"
                            : "border-gray-300 hover:border-gray-600"
                    }`}
                >
                    <h3
                        className={`text-[16px] md:text-[20px] font-[600] font-sora transition-colors duration-300 ${
                            activeIndex === 3
                                ? "text-[#01386A]"
                                : "text-[#0000008C] group-hover:text-gray-600"
                        }`}
                    >
                        Dedicated ServiceNow HRSD Team
                    </h3>

                    <ChevronRight
                        size={24}
                        className={`transition-all duration-300 ${
                            activeIndex === 3
                                ? "opacity-100 translate-x-0 text-[#ED6800]"
                                : "opacity-0 -translate-x-4 text-gray-300"
                        }`}
                    />
                </div>

                {/* INTEGRATIONS */}
                <div
                    onClick={() => setActiveIndex(4)}
                    className={`group cursor-pointer flex items-center justify-between py-6 lg:py-8 border-b-[3px] transition-all duration-300 ${
                        activeIndex === 4
                            ? "border-[#ED6800]"
                            : "border-gray-300 hover:border-gray-600"
                    }`}
                >
                    <h3
                        className={`text-[16px] md:text-[20px] font-[600] font-sora transition-colors duration-300 ${
                            activeIndex === 4
                                ? "text-[#01386A]"
                                : "text-[#0000008C] group-hover:text-gray-600"
                        }`}
                    >
                        Dedicated ServiceNow Integration Team
                    </h3>

                    <ChevronRight
                        size={24}
                        className={`transition-all duration-300 ${
                            activeIndex === 4
                                ? "opacity-100 translate-x-0 text-[#ED6800]"
                                : "opacity-0 -translate-x-4 text-gray-300"
                        }`}
                    />
                </div>

                {/* CUSTOM DEVELOPMENT */}
                <div
                    onClick={() => setActiveIndex(5)}
                    className={`group cursor-pointer flex items-center justify-between py-6 lg:py-8 border-b-[3px] transition-all duration-300 ${
                        activeIndex === 5
                            ? "border-[#ED6800]"
                            : "border-gray-300 hover:border-gray-600"
                    }`}
                >
                    <h3
                        className={`text-[16px] md:text-[20px] font-[600] font-sora transition-colors duration-300 ${
                            activeIndex === 5
                                ? "text-[#01386A]"
                                : "text-[#0000008C] group-hover:text-gray-600"
                        }`}
                    >
                        Dedicated ServiceNow Development Team
                    </h3>

                    <ChevronRight
                        size={24}
                        className={`transition-all duration-300 ${
                            activeIndex === 5
                                ? "opacity-100 translate-x-0 text-[#ED6800]"
                                : "opacity-0 -translate-x-4 text-gray-300"
                        }`}
                    />
                </div>

            </div>

            {/* RIGHT SIDE */}
            <div className="lg:w-1/2 flex flex-col justify-start">

                <div className="min-h-[100px] mb-8">

                    {activeIndex === 0 && (
                        <p className="text-[#49577A] text-[14px] lg:text-[16px] leading-relaxed font-[500] font-graphik animate-fade-in">
                            Build a dedicated ServiceNow ITSM team to support
                            incident, problem, change, request, and knowledge
                            management. Your dedicated professionals can work on
                            workflows, automation, configuration, enhancements,
                            and ongoing IT service management requirements.
                        </p>
                    )}

                    {activeIndex === 1 && (
                        <p className="text-[#49577A] text-[14px] lg:text-[16px] leading-relaxed font-[500] font-graphik animate-fade-in">
                            Add dedicated ServiceNow ITOM and CMDB professionals
                            for discovery, service mapping, configuration
                            management, event management, infrastructure
                            visibility, and IT operations initiatives.
                        </p>
                    )}

                    {activeIndex === 2 && (
                        <p className="text-[#49577A] text-[14px] lg:text-[16px] leading-relaxed font-[500] font-graphik animate-fade-in">
                            Build a dedicated ServiceNow CSM team to support case
                            management, customer workflows, portals, automation,
                            integrations, configuration, and ongoing customer
                            service platform enhancements.
                        </p>
                    )}

                    {activeIndex === 3 && (
                        <p className="text-[#49577A] text-[14px] lg:text-[16px] leading-relaxed font-[500] font-graphik animate-fade-in">
                            Strengthen HR service delivery with a dedicated
                            ServiceNow HRSD team supporting employee workflows,
                            case management, HR services, integrations,
                            automation, configuration, and ongoing enhancements.
                        </p>
                    )}

                    {activeIndex === 4 && (
                        <p className="text-[#49577A] text-[14px] lg:text-[16px] leading-relaxed font-[500] font-graphik animate-fade-in">
                            Build a dedicated ServiceNow integration team to
                            connect ServiceNow with enterprise applications using
                            REST and SOAP APIs, IntegrationHub, data
                            synchronization, third-party systems, and automated
                            workflows.
                        </p>
                    )}

                    {activeIndex === 5 && (
                        <p className="text-[#49577A] text-[14px] lg:text-[16px] leading-relaxed font-[500] font-graphik animate-fade-in">
                            Build a dedicated ServiceNow development team for
                            custom applications, scripting, business rules, client
                            scripts, UI policies, workflows, portals,
                            integrations, automation, and ongoing platform
                            development.
                        </p>
                    )}

                </div>

                <div className="w-full h-[300px] lg:h-[378px] overflow-hidden relative">

                    <div className="absolute inset-0 bg-gradient-to-tr from-blue-100/50 to-transparent z-10 pointer-events-none"></div>

                    {activeIndex === 0 && (
                        <img
                            src="/assets/home-/ai-2.png"
                            alt="Dedicated ServiceNow ITSM team"
                            className="w-full h-full object-cover transition-opacity duration-500 animate-[fadeIn_0.5s_ease-in-out_forwards]"
                        />
                    )}

                    {activeIndex === 1 && (
                        <img
                            src="/assets/home-/ai-3.png"
                            alt="Dedicated ServiceNow ITOM and CMDB team"
                            className="w-full h-full object-cover transition-opacity duration-500 animate-[fadeIn_0.5s_ease-in-out_forwards]"
                        />
                    )}

                    {activeIndex === 2 && (
                        <img
                            src="/assets/home-/ai-4.png"
                            alt="Dedicated ServiceNow CSM team"
                            className="w-full h-full object-cover transition-opacity duration-500 animate-[fadeIn_0.5s_ease-in-out_forwards]"
                        />
                    )}

                    {activeIndex === 3 && (
                        <img
                            src="/assets/home-/img1.png"
                            alt="Dedicated ServiceNow HRSD team"
                            className="w-full h-full object-cover transition-opacity duration-500 animate-[fadeIn_0.5s_ease-in-out_forwards]"
                        />
                    )}

                    {activeIndex === 4 && (
                        <img
                            src="/assets/home-/img2.png"
                            alt="Dedicated ServiceNow integration team"
                            className="w-full h-full object-cover transition-opacity duration-500 animate-[fadeIn_0.5s_ease-in-out_forwards]"
                        />
                    )}

                    {activeIndex === 5 && (
                        <img
                            src="/assets/home-/img3.png"
                            alt="Dedicated ServiceNow development team"
                            className="w-full h-full object-cover transition-opacity duration-500 animate-[fadeIn_0.5s_ease-in-out_forwards]"
                        />
                    )}

                </div>

            </div>

        </div>
    </div>

    <style>{`
        @keyframes fadeIn {
            from {
                opacity: 0;
                transform: translateY(10px);
            }

            to {
                opacity: 1;
                transform: translateY(0);
            }
        }

        .animate-fade-in {
            animation: fadeIn 0.5s ease-out forwards;
        }
    `}</style>

</section>




<section className="w-full pb-[40px] md:pb-[100px] bg-white">
    <div className="max-w-[1440px] mx-auto px-[20px] md:px-[40px] lg:px-[80px]">

        <div className="flex flex-col lg:flex-row items-start gap-[24px] md:gap-[40px] lg:gap-[80px]">

            {/* LEFT SIDE */}
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{
                    duration: 0.8,
                    ease: "easeOut"
                }}
                className="w-full lg:w-5/12 lg:sticky lg:top-[120px]"
            >
                <h2 className="font-sora text-[20px] lg:text-[30px] font-[600] text-[#01386A] tracking-tight leading-[1.2] lg:max-w-[350px]">
                    Frequently Asked Questions About ServiceNow Staff Augmentation
                </h2>
            </motion.div>


            {/* RIGHT SIDE */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{
                    once: true,
                    amount: 0.1
                }}
                transition={{
                    duration: 0.6,
                    ease: "easeOut"
                }}
                className="w-full lg:w-7/12 flex flex-col border border-[#E4E8F5] bg-white rounded-sm"
            >

                {servicenowFaqData.map((faq, index) => {

                    const isActive = faqActiveIndex === index;

                    return (
                        <div
                            key={faq.id}
                            className={`flex flex-col border-b ${
                                isActive
                                    ? "border-[#ED6800] border-b-[3px] md:border-b-[4px]"
                                    : "border-[#E4E8F5]"
                            } last:border-b-0 transition-colors duration-300`}
                        >

                            {/* QUESTION */}
                            <button
                                type="button"
                                onClick={() => toggleFaqAccordion(index)}
                                className="w-full flex justify-between items-center text-left py-[18px] sm:py-[24px] md:py-[32px] px-[16px] sm:px-[24px] md:px-[40px] group cursor-pointer bg-white md:hover:bg-[#F8FAFF] transition-colors duration-300"
                                aria-expanded={isActive}
                            >

                                <h3
                                    className={`font-sora max-w-[85%] text-[16px] md:text-[20px] font-[600] pr-[16px] sm:pr-[20px] transition-colors duration-300 ${
                                        isActive
                                            ? "text-[#01386A]"
                                            : "text-[#01386A] md:group-hover:text-[#ED6800]"
                                    }`}
                                >
                                    {faq.question}
                                </h3>


                                {/* PLUS / MINUS ICON */}
                                <div className="flex-shrink-0 text-[#01386A] transition-colors duration-300 md:group-hover:text-[#ED6800]">

                                    {isActive ? (

                                        <svg
                                            width="18"
                                            height="18"
                                            className="md:w-[20px] md:h-[20px]"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            aria-hidden="true"
                                        >
                                            <line
                                                x1="5"
                                                y1="12"
                                                x2="19"
                                                y2="12"
                                            />
                                        </svg>

                                    ) : (

                                        <svg
                                            width="18"
                                            height="18"
                                            className="md:w-[20px] md:h-[20px]"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            aria-hidden="true"
                                        >
                                            <line
                                                x1="12"
                                                y1="5"
                                                x2="12"
                                                y2="19"
                                            />

                                            <line
                                                x1="5"
                                                y1="12"
                                                x2="19"
                                                y2="12"
                                            />
                                        </svg>

                                    )}

                                </div>

                            </button>


                            {/* ANSWER */}
                            <AnimatePresence initial={false}>

                                {isActive && (

                                    <motion.div
                                        initial={{
                                            height: 0,
                                            opacity: 0
                                        }}
                                        animate={{
                                            height: "auto",
                                            opacity: 1
                                        }}
                                        exit={{
                                            height: 0,
                                            opacity: 0
                                        }}
                                        transition={{
                                            duration: 0.4,
                                            ease: "easeInOut"
                                        }}
                                        className="overflow-hidden bg-white"
                                    >

                                        <div className="pb-[20px] sm:pb-[32px] px-[16px] sm:px-[24px] md:px-[40px]">

                                            <p className="font-graphik text-[#6C7689] text-[14px] md:text-[16px] font-[500] leading-[1.6]">
                                                {faq.answer}
                                            </p>

                                        </div>

                                    </motion.div>

                                )}

                            </AnimatePresence>

                        </div>
                    );
                })}

            </motion.div>

        </div>

    </div>
</section>
  <ServiceNowLeadForm/>

<Banner
    title="Dedicated ServiceNow Team"
    text="Build a dedicated ServiceNow team with experienced developers, consultants, architects, administrators, integration specialists, and QA professionals aligned with your project and long-term business requirements."
/>
    </div>
  )
}


export default DedicatedServiceNowTeam
